import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { AppDispatch } from '../../../core/data/redux/store';
import { loginStart, loginSuccess, loginFailure } from '../../../core/data/redux/authSlice';
import { sendOTP, verifyOTP } from '../../../core/data/api/authService';
import { fetchUsersApi } from "../../../core/data/services/userService";
import { fetchusersSuccess, fetchuserDetailSuccess } from "../../../core/data/redux/userSlice";
import { fetchUserDetailApi } from "../../../core/data/services/teacherService";

const Login = () => {
  const [mobile, setMobile] = useState("");
  const [, setOtp] = useState<number | null>(null);
  const [otpVal, setOtpVal] = useState<string[]>(new Array(6).fill(''));
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const textBase = useRef<HTMLDivElement>(null);
  const otpInputsRef = useRef<HTMLInputElement[]>([]);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Debug function to test console logging
  useEffect(() => {
    console.log("Login component mounted");
  }, []);

  // Generate OTP on component mount (for testing)
  useEffect(() => {
    setOtp(123456); // Keep this for testing, remove in production
  }, []);

  // Timer countdown logic
  useEffect(() => {
    if (timer > 0 && showOtpSection) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, showOtpSection]);

  // Focus on the first OTP input when the OTP section is shown
  useEffect(() => {
    if (showOtpSection && otpInputsRef.current[0]) {
      setTimeout(() => {
        otpInputsRef.current[0]?.focus();
      }, 100);
    }
  }, [showOtpSection]);

  // Generate a new OTP and reset the timer
  const generateOTP = async () => {
    try {
      setIsLoading(true);
      const sendOtpResponse = await sendOTP({ phone: mobile });

      if (sendOtpResponse && (sendOtpResponse.status === "success" || sendOtpResponse.statusCode === 200)) {
        setOtp(Math.floor(100000 + Math.random() * 900000)); // Generate 6-digit OTP for testing
        setTimer(30);
        setCanResend(false);
        setOtpVal(new Array(6).fill(''));

        // Clear any error styling
        if (textBase.current?.classList.contains("otp-error")) {
          textBase.current.classList.remove("otp-error");
        }

        alert("OTP has been resent successfully!");
      } else {
        throw new Error(sendOtpResponse?.message || "Failed to resend OTP");
      }
    } catch (error: any) {
      console.error("Resend OTP error:", error);
      alert(error.message ?? "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending OTP
  const handleSendOTP = async () => {
    if (mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setIsLoading(true);
      dispatch(loginStart());

      const sendOtpResponse = await sendOTP({ phone: mobile });

      if (sendOtpResponse && (sendOtpResponse.status === "success" || sendOtpResponse.statusCode === 200)) {
        setShowOtpSection(true);
        setTimer(30);
        setCanResend(false);
        setOtpVal(new Array(6).fill(''));
      } else {
        throw new Error(sendOtpResponse?.message || "Failed to send OTP - Invalid response");
      }
    } catch (error: any) {
      console.error("Send OTP Error:", error);
      dispatch(loginFailure(error.message ?? "Failed to send OTP"));
      alert(error.message ?? "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Role mapping function
  const getRoleName = (roleId: number): string => {
    const roleMap: { [key: number]: string } = {
      1: 'admin',
      2: 'state-admin',
      3: 'district-admin',
      4: 'block-admin',
      5: 'school-admin',
      7: 'hm',
      8: 'teacher',
      9: 'mealSupervisor',
      12: 'village-admin',
    };
    return roleMap[roleId] || 'unknown';
  };

  const validateOtp = async (otpToValidate?: string[]) => {
    try {
      setIsLoading(true);
      dispatch(loginStart());

      const enteredOtp = otpToValidate ? otpToValidate.join("") : otpVal.join("");


      if (enteredOtp.length !== 6) {
        throw new Error("Please enter complete 6-digit OTP");
      }

      // Remove error styling
      if (textBase.current?.classList.contains("otp-error")) {
        textBase.current.classList.remove("otp-error");
      }

      // For testing, you can use the entered OTP or the generated OTP
      const otpToVerify = parseInt(enteredOtp); // Use entered OTP
      // const otpToVerify = otp; // Or use generated OTP for testing
      const data = await verifyOTP({ phone: mobile, otp: otpToVerify });

      if (!data || !data.data || !data.data.accessToken) {
        console.error("Invalid response structure:", data);
        throw new Error("Invalid response from server - missing access token");
      }

      // Extract data from the nested structure
      const { userId, roleIds, accessToken } = data.data;
      const roleId = roleIds?.[0];
      const roleName = getRoleName(roleId);

      // Dispatch login success
      dispatch(loginSuccess({
        role: Array.isArray(roleIds) ? (roleIds.length > 0 ? roleIds[0] : []) : (roleIds ? roleIds : []),
        accessToken: accessToken,
        userId: userId,
        roleName: roleName
      }));
 
      // Fetch user details
      try {
        const userDetail = await fetchUserDetailApi({ userId: userId });
        dispatch(fetchuserDetailSuccess(userDetail));

        const user = await fetchUsersApi({ userId: userId });
        dispatch(fetchusersSuccess(user));

        // Navigate to dashboard
        navigate(`/${ roleName }/dashboard`);
      } catch (fetchError) {
        console.error("Error fetching user data:", fetchError);
        // Still navigate even if user fetch fails
        navigate(`/${ roleName }/dashboard`);
      }

    } catch (error: any) {
      console.error("Validate OTP Error:", error);

      // Add error styling
      if (textBase.current) {
        textBase.current.classList.add("otp-error");
      }

      dispatch(loginFailure(error.message || "OTP verification failed"));
      alert(error.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP input changes with auto-validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;

    // Only allow numeric input
    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newOtpVal = [...otpVal];
    newOtpVal[index] = value;
    setOtpVal(newOtpVal);

    // Remove error styling when user starts typing
    if (textBase.current?.classList.contains("otp-error")) {
      textBase.current.classList.remove("otp-error");
    }

    // Move focus forward
    if (value && index < 5) {
      setTimeout(() => {
        otpInputsRef.current[index + 1]?.focus();
      }, 0);
    }

    // Auto-validate when all fields are filled
    if (newOtpVal.every(val => val !== '') && newOtpVal.length === 6) {
      // Use a slight delay to ensure state is updated
      setTimeout(() => {
        validateOtp(newOtpVal);
      }, 100);
    }
  };

  // Handle backspace for better UX
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      const newOtpVal = [...otpVal];

      if (newOtpVal[index]) {
        // Clear current field
        newOtpVal[index] = '';
        setOtpVal(newOtpVal);
      } else if (index > 0) {
        // Move to previous field and clear it
        newOtpVal[index - 1] = '';
        setOtpVal(newOtpVal);
        setTimeout(() => {
          otpInputsRef.current[index - 1]?.focus();
        }, 0);
      }
    }
  };

  return (
    <div className="container-fluid">
      <div className="w-100 position-relative flex-wrap d-block vh-100">
        <div className="row vh-100">
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="login-bg">
              <div className="text-center">
                {/* Logo can be added here */}
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 mx-auto p-4 d-flex align-items-center justify-content-center">
            <div className="row w-100">
              <div className="col-lg-10 col-md-10 mx-auto p-4 d-flex align-items-center justify-content-center">
                <form className="w-100" onSubmit={(e) => e.preventDefault()}>
                  <div className="text-center mb-2">
                    <ImageWithBasePath
                      src="assets/img/authentication/PM-Logo.svg"
                      className="img-fluid"
                      alt="Logo"
                      width={80}
                    />
                  </div>
                  {!showOtpSection ? (
                    <div>
                      <h2 className="mt-2 mb-2 text-center">Welcome</h2>
                      <p className="mb-4 text-center">
                        Please enter your mobile number to sign in
                      </p>
                      <label className="form-label">Mobile Number</label>
                      <div className="input-icon mb-3 position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-phone" />
                        </span>
                        <input
                          type="tel"
                          className="form-control"
                          value={mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setMobile(value);
                          }}
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          disabled={isLoading}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleSendOTP}
                        disabled={mobile.length !== 10 || isLoading}
                      >
                        {isLoading ? "Sending..." : "Send OTP"}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <h2 className="mb-2 text-center">Verification</h2>
                      <p className="mb-5 text-center">
                        Please enter the OTP sent to +91 {mobile}
                      </p>

                      <div className="input-icon mb-3 position-relative">
                        <div className="otp-base" ref={textBase}>
                          {Array.from({ length: 6 }, (_, index) => (
                            <input
                              ref={(el) => {
                                if (el) otpInputsRef.current[index] = el;
                              }}
                              key={index}
                              type="text"
                              maxLength={1}
                              className="otp-input"
                              value={otpVal[index] || ''}
                              onChange={(e) => handleInputChange(e, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              disabled={isLoading}
                            />
                          ))}
                        </div>
                      </div>

                      <p className="text-center mb-3">
                        {timer > 0
                          ? `Resend OTP in ${ timer }s`
                          : "You can now resend OTP"}
                      </p>

                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary w-100"
                          onClick={generateOTP}
                          disabled={!canResend || isLoading}
                        >
                          {isLoading ? "Sending..." : "Resend OTP"}
                        </button>
                      </div>

                      <button
                        type="button"
                        className="btn btn-link w-100 mt-2"
                        onClick={() => {
                          setShowOtpSection(false);
                          setOtpVal(new Array(6).fill(''));
                          setTimer(30);
                          setCanResend(false);
                        }}
                      >
                        Change Mobile Number
                      </button>
                    </div>
                  )}

                  <div className="text-center mt-3">                    <ImageWithBasePath
                      src="assets/img/authentication/power.svg"
                      className="img-fluid"
                      alt="Logo"
                      width={60}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;